import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Search, Loader2, CheckCircle2, Table2 } from 'lucide-react';

/**
 * AI Wizard dialog — extracted from SemanticModelsPage.
 * 3-step flow: select tables -> AI classify -> review and finish.
 */
export function AiWizardDialog({
  open, onOpenChange,
  wizardStep, setWizardStep,
  wizardTables,
  wizardSelectedTables, setWizardSelectedTables,
  wizardTableSearch, setWizardTableSearch,
  wizardTablesLoading,
  saving,
  aiClassifying,
  entities,
  relationships,
  onAddEntities,
  onRunAi,
  onFinish,
}) {
  const filteredTables = wizardTables.filter(t =>
    !wizardTableSearch || t.table_name.toLowerCase().includes(wizardTableSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {wizardStep === 1 && 'Step 1: Select Entities'}
            {wizardStep === 2 && 'Step 2: AI Field Classification'}
            {wizardStep === 3 && 'Step 3: Review & Finish'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${wizardStep >= s ? 'bg-primary' : 'bg-muted/30'}`} />
          ))}
        </div>
        <ScrollArea className="flex-1">
          {wizardStep === 1 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <p className="text-xs text-muted-foreground">Select which tables to include as entities in your model. All are selected by default.</p>
              {wizardTablesLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                  <p className="text-sm text-muted-foreground">Discovering tables and columns...</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">This may take a moment for large databases</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input value={wizardTableSearch || ''} onChange={e => setWizardTableSearch(e.target.value)} placeholder="Search tables..." className="pl-8 h-8 text-xs" />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setWizardSelectedTables(wizardTables.map(t => t.table_name))} className="text-xs h-8">All</Button>
                    <Button variant="outline" size="sm" onClick={() => setWizardSelectedTables([])} className="text-xs h-8">None</Button>
                    <Badge variant="secondary" className="text-[9px]">{wizardSelectedTables.length}/{wizardTables.length}</Badge>
                  </div>
                  <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
                    {filteredTables.map(t => {
                      const isSelected = wizardSelectedTables.indexOf(t.table_name) >= 0;
                      return (
                        <button
                          key={t.table_name}
                          onClick={() => setWizardSelectedTables(prev => isSelected ? prev.filter(n => n !== t.table_name) : prev.concat([t.table_name]))}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${isSelected ? 'border-primary/30 bg-primary/5' : 'border-border/30 opacity-50'}`}
                        >
                          <div className={`w-5 h-5 rounded-md border flex items-center justify-center text-xs ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-border/50'}`}>
                            {isSelected && <CheckCircle2 className="w-3 h-3" />}
                          </div>
                          <Table2 className="w-4 h-4 text-emerald-400" />
                          <div>
                            <p className="text-xs font-semibold text-foreground">{t.table_name}</p>
                            <p className="text-[10px] text-muted-foreground">{(t.columns || []).length} columns{t.rows ? ` \u00b7 ${t.rows.toLocaleString()} rows` : ''}</p>
                          </div>
                        </button>
                      );
                    })}
                    {filteredTables.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-4">{wizardTables.length > 0 ? 'No tables match search' : 'No tables discovered from data source'}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {wizardStep === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className="text-xs text-muted-foreground">AI will analyze your data and suggest which fields are dimensions vs measures, and propose relationships between entities.</p>
              <div className="glass-card rounded-xl p-6 text-center">
                <Sparkles className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
                <p className="text-sm font-heading font-semibold text-foreground mb-1">AI Classification</p>
                <p className="text-xs text-muted-foreground mb-4">This will analyze {entities.length} entities and suggest field types and relationships</p>
                <Button onClick={onRunAi} disabled={aiClassifying} className="rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white active:scale-95" data-testid="wizard-ai-btn">
                  {aiClassifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {aiClassifying ? 'Classifying...' : 'Run AI Classification'}
                </Button>
              </div>
            </div>
          )}
          {wizardStep === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <div className="glass-card rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <p className="text-sm font-heading font-semibold text-foreground">Model Ready</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div><p className="text-lg font-heading font-bold text-foreground">{entities.length}</p><p className="text-[10px] text-muted-foreground">Entities</p></div>
                  <div><p className="text-lg font-heading font-bold text-foreground">{entities.reduce((a, e) => a + (e.attributes || []).length, 0)}</p><p className="text-[10px] text-muted-foreground">Fields</p></div>
                  <div><p className="text-lg font-heading font-bold text-foreground">{relationships.length}</p><p className="text-[10px] text-muted-foreground">Relationships</p></div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Your model is ready. You can fine-tune field classifications, add calculated fields, and adjust relationships in the ER editor.</p>
            </div>
          )}
        </ScrollArea>
        <div className="flex justify-between pt-3 border-t border-border/30 mt-3">
          {wizardStep > 1 ? (
            <Button variant="outline" onClick={() => setWizardStep(s => s - 1)} className="rounded-lg text-xs">Back</Button>
          ) : <div />}
          {wizardStep === 1 && (
            <Button onClick={onAddEntities} disabled={saving || wizardSelectedTables.length === 0} className="rounded-lg bg-primary text-primary-foreground text-xs active:scale-95">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Add {wizardSelectedTables.length} Entities & Continue
            </Button>
          )}
          {wizardStep === 2 && (
            <Button variant="outline" onClick={() => setWizardStep(3)} className="rounded-lg text-xs">Skip AI & Continue</Button>
          )}
          {wizardStep === 3 && (
            <Button onClick={onFinish} className="rounded-lg bg-primary text-primary-foreground text-xs active:scale-95">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Open ER Editor
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
